import { useTranslation } from 'react-i18next';

import { YUP_SCHEMA } from '@repo/constants';
import { FormikHelpers, FormikProps, useFormik } from 'formik';
import styled from 'styled-components';
import * as Yup from 'yup';

import '../../i18n';
import InputFormikWithLimit from '../../TextField/InputFormikWithLimit';

interface VerifiedModalProps {
  email?: string;
  subTitle?: string;
  isVerified: boolean;
  isRefetchMyProfile?: boolean;
  onClickSubmit: (email: string) => Promise<void>;
  closeModal: () => void;
  onControlError: (error: unknown, formik?: FormikProps<{ email: string | undefined }>) => void;
}

export default function EmailVerifyBody({
  email,
  subTitle,
  isVerified,
  onClickSubmit,
  closeModal,
  onControlError,
}: VerifiedModalProps) {
  const { t } = useTranslation();
  const FIRST_VERIFY = !isVerified;

  const emailFormik = useFormik({
    initialValues: {
      email: !isVerified ? email : '',
    },
    validationSchema: Yup.object({
      email: YUP_SCHEMA.EMAIL,
    }),
    onSubmit: async (values) => {
      if (!values.email) return;

      const email = values.email.toLowerCase();
      await onClickSubmit(email).catch((err) => {
        onControlError(err.response?.data, emailFormik);
      });
    },
  });

  return (
    <SWrapper>
      <STitleWrapper>
        <STitleContainer>
          <STitle>
            {FIRST_VERIFY ? t('setting.edit_email_verify_message') : t('setting.edit_email_re_verify_message')}
          </STitle>
        </STitleContainer>
        {subTitle && <SSubTitleContainer>{t(subTitle)}</SSubTitleContainer>}
      </STitleWrapper>
      <SInputBox>
        <SUserEmail
          formik={emailFormik}
          name="email"
          label={''}
          placeholder={isVerified ? email : t('setting.edit_email_verify_placeholder')}
          $isError={!!emailFormik.errors.email}
          autoComplete="off"
        />
        <SValidationSentence $isError={Boolean(emailFormik.touched.email && emailFormik.errors.email)}>
          {t(emailFormik.errors.email ?? '')}
        </SValidationSentence>
      </SInputBox>

      <SWebModalButtonContainer>
        <button type="button" onClick={closeModal}>
          {t('general.cancel')}
        </button>
        <button
          type="button"
          disabled={!emailFormik.values?.email || !emailFormik?.isValid}
          onClick={() => emailFormik.values?.email && emailFormik?.handleSubmit()}
        >
          {t('setting.edit_email_verify_verify_now_button')}
        </button>
      </SWebModalButtonContainer>
    </SWrapper>
  );
}

const SInputBox = styled.div`
  width: 100%;
  max-width: calc(400px - 80px);
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
`;

const STitleWrapper = styled.div`
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  width: 100%;
  position: relative;
`;

const STitleContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
`;

const STitle = styled.p`
  ${({ theme }) => theme.font.head5_sb};

  @media ${({ theme }) => theme.device.medium} {
    ${({ theme }) => theme.font.body1_sb};
  }
`;

const SSubTitleContainer = styled.div`
  color: ${({ theme }) => theme.color.text.TXT_3};
  ${({ theme }) => theme.font.body2_r};
`;

const SWrapper = styled.div`
  padding: 20px 24px 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  border-radius: 12px;
  background-color: ${({ theme }) => theme.color.popup.background};
`;

type SUserEmailProps = {
  $isError: boolean;
};
const SUserEmail = styled(InputFormikWithLimit)<SUserEmailProps>`
  width: 100%;

  input {
    border-radius: 8px;
    position: relative;
    padding: 13px 12px;
    height: 48px;
    ${({ theme }) => theme.font.body2_r};
    color: ${({ theme }) => theme.color.text.TXT_4};
    background-color: ${({ theme }) => theme.color.popup.input};

    &:focus {
      outline: none;
      border: 1px solid ${({ theme, $isError }) => ($isError ? theme.color.static.warning : theme.color.static.primary)};
    }
  }

  .reset-btn {
    width: 20px;
    height: 20px;
    bottom: 13px;
    right: 12px;
  }
`;

type SValidationSentenceProps = {
  $isError: boolean;
};
const SValidationSentence = styled.ul<SValidationSentenceProps>`
  font-size: 12px;
  color: ${({ theme, $isError }) => ($isError ? theme.color.static.warning : theme.color.text.TXT_1)};
  font-weight: 400;
  line-height: 16px;
  text-align: left;
  padding-left: 12px;
`;

const SWebModalButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  width: 100%;

  button {
    display: flex;
    height: 46px;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    box-sizing: border-box;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
  }
  button:nth-child(1) {
    width: 120px;
    min-width: 120px;
    color: ${({ theme }) => theme.color.text.TXT_1};
    border: 1px solid ${({ theme }) => theme.color.popup.btn_disable};
  }

  button:nth-child(2) {
    width: 190px;
    font-weight: 500;
    background-color: ${({ theme }) => theme.color.static.primary};
    color: ${({ theme }) => theme.color.static.white};

    &:disabled {
      background-color: ${({ theme }) => theme.color.popup.btn_disable};
      font-weight: 400;
    }
  }
`;
